import {sha256} from 'js-sha256';
import {useSearchParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import apiCall, {setUserToken} from '../../utils/api';
import Loader from '../../shared/Loader';
import CustomSelect from '../../shared/CustomSelect';
import Genere from './beneficiaryCharts/Genere';
import AgeChart from './beneficiaryCharts/AgeChart';
import BeneficiaryCounter from './beneficiaryCharts/BeneficiaryCounter';
import OriginTable from './beneficiaryCharts/OriginTable';
import Progetti from './beneficiaryCharts/Progetti';
import GradoScolastico from './columnCharts/GradoScolastico';
import Precarita from './columnCharts/Precarita';
import Istruzione from './columnCharts/Istruzione';
import {IFieldProps} from '@/resources/Field';
import {IFieldValueProps, IUoaProps} from '@/containers/PavesiHomepage/utils/models';

import './styles.scss';

const KEY_PROJECT = 'progetto';
const KEY_GENDER = 'genere';
const KEY_AGE = 'data-di-nascita';
const KEY_ORIGIN = 'paese-di-origine';

const PavesiHomepage = () => {
    const [searchParams] = useSearchParams();
    const container = searchParams.get('container_id');
    const beneficiaryType = searchParams.get('beneficiary');
    const hashKey = searchParams.get('key');
    const survey_id = searchParams.get('survey_id');
    const hashKeyLocal = sha256(process.env.REACT_APP_HASH_KEY_PAVESI_HOMEPAGE || '');

    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [submissions, setSubmissions] = useState([]);
    const [uoas, setUoas] = useState<IUoaProps[]>([]);

    const [selectedCheckpoint, setSelectedCheckpoint] = useState<any>('All');
    const [selectedProject, setSelectedProject] = useState<string>('All');
    const [beginningCheckpoints, setBeginningCheckpoints] = useState<any>([]);

    const [checkpoints, setCheckpoints] = useState<any>([]);
    const [projectField, setProjectField] = useState<IFieldProps | undefined>(undefined);
    const [genderField, setGenderField] = useState<IFieldProps | undefined>(undefined);
    const [ageField, setAgeField] = useState<IFieldProps | undefined>(undefined);
    const [originField, setOriginField] = useState<IFieldProps | undefined>(undefined);

    const [projects, setProjects] = useState<IFieldValueProps[]>([]);
    const [genders, setGenders] = useState<IFieldValueProps[]>([]);
    const [origins, setOrigins] = useState<IFieldValueProps[]>([]);
    const [beneficiaryCount, setBeneficiaryCount] = useState<number>(0);

    setUserToken(searchParams.get('token') || '');

    const getFilterData = async () => {
        const requests = [
            await apiCall({
                url: `containers/${container}/checkpoints`,
                params: {
                    container_id: 3,
                },
            }),
            await apiCall({
                url: `containers/${container}/fields`,
            }),
        ];

        const responses = await Promise.all(requests);
        const fields: IFieldProps[] = responses[1].data.custom_fields ?? [];

        setCheckpoints(responses[0]?.data?.checkpoints);
        setProjectField(fields.find(field => field.key === KEY_PROJECT));
        setGenderField(fields.find(field => field.key === KEY_GENDER));
        setAgeField(fields.find(field => field.key === KEY_AGE));
        setOriginField(fields.find(field => field.key === KEY_ORIGIN));
    };

    const getSurvey = async () => {
        try {
            if (survey_id && hashKey === hashKeyLocal) {
                setLoading(true);
                const requests = [
                    await apiCall({
                        url: `containers/${container}/uoa/search`,
                        params: {
                            container_id: container,
                            pagination: 'false',
                            type_id: beneficiaryType,
                            ...(selectedCheckpoint !== 'All' && {checkpoint_ids: [selectedCheckpoint]}),
                        },
                    }),
                    await apiCall({
                        url: `containers/${container}/submissions`,
                        params: {
                            survey_id,
                            additional_data: false,
                            ...(projectField && selectedProject !== 'All' && {custom_fields: {[`field_${projectField.id}`]: selectedProject}}),
                            ...(selectedCheckpoint !== 'All' && {checkpoint_id: selectedCheckpoint}),
                        },
                    }),
                ];

                const responses = await Promise.all(requests);

                if (projectField && selectedProject !== 'All') {
                    setUoas(
                        (responses[0]?.data.data ?? []).filter((uoa: IUoaProps) => uoa.custom_field_values
                            .some((fieldValue) =>
                                fieldValue.field_id === projectField.id && Number(fieldValue.stored_value) === Number(selectedProject)
                            )
                        )
                    );
                } else {
                    setUoas(responses[0]?.data.data);
                }

                setSubmissions(responses[1]?.data?.submissions);
            }
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    };

    const processData = () => {
        setBeneficiaryCount(uoas.length);

        if (projectField) {
            setProjects(getOptionCount(projectField));
        }

        if (genderField) {
            setGenders(getOptionCount(genderField));
        }

        if (originField) {
            setOrigins(getOptionCount(originField));
        }
    }

    const getOptionCount = (field: IFieldProps) => {
        const value: IFieldValueProps[] = [];

        field.options.forEach(option => {
            value.push({
                name: option.name,
                value: uoas.filter(uoa => uoa.custom_field_values
                    .some((fieldValue) =>
                        fieldValue.field_id === field.id && Number(fieldValue.stored_value) === option.id
                    )
                ).length
            });
        });

        return value;
    }

    useEffect(() => {
        getFilterData().then((_) => setIsReady(true));
    }, []);

    useEffect(() => {
        setBeginningCheckpoints(checkpoints.filter((checkpoint: any) => checkpoint.name.includes('INIZIO')));
    }, [checkpoints]);

    useEffect(() => {
        if (isReady) {
            getSurvey().then((_) => setLoading(false));
        }
    }, [selectedProject, selectedCheckpoint, isReady]);

    useEffect(() => {
        processData();
    }, [uoas]);

    return (
        <div className='pavesi-homepage'>
            {!loading && (
                <>
                    <div className='filter space-x-4'>
                        {
                            projectField && <CustomSelect
                                items={projectField.options}
                                value={selectedProject}
                                onChange={(value: any) => setSelectedProject(value)}
                                label='Progetti'
                                className='project-select'
                                defaultVariant='All'
                            />
                        }
                        <CustomSelect
                            items={checkpoints}
                            value={selectedCheckpoint}
                            onChange={(value: any) => setSelectedCheckpoint(value)}
                            label='Checkpoints'
                            className='project-select'
                            defaultVariant='All'
                        />
                    </div>

                    <div className='beneficiary-charts'>
                        <div className='beneficiary-charts__item'>
                            <div>
                                <div className='section-title'>Maschi / Femmine</div>
                                {genders && <Genere data={genders}/>}
                            </div>
                            <div>
                                <div className='section-title'>Fascia d'età</div>
                                {ageField && <AgeChart ageField={ageField} uoas={uoas}/>}
                            </div>
                            <div>
                                <div className='section-title'>Attività partecipate</div>
                                {projects && (
                                    <Progetti
                                        data={projects}
                                    />
                                )}
                            </div>
                        </div>
                        <div className='beneficiary-charts__item'>
                            <div>
                                <div className='section-title'>Beneficiari</div>
                                {beneficiaryCount && <BeneficiaryCounter data={beneficiaryCount}/>}
                            </div>
                            {origins && (
                                <OriginTable
                                    data={origins}
                                    total={beneficiaryCount}
                                />
                            )}
                        </div>
                    </div>

                    <div className='column-charts'>
                        <div className='column-charts__row'>
                            <div>
                                <div className='section-title'>Grado scolastico</div>
                                <GradoScolastico
                                    submissions={submissions}
                                    checkpoints={beginningCheckpoints}
                                />
                            </div>
                            <div>
                                <div className='section-title'>Livello reddituale della famiglia</div>
                                <Precarita
                                    submissions={submissions}
                                    checkpoints={beginningCheckpoints}
                                />
                            </div>
                        </div>
                        <div className='column-charts__row'>
                            <div>
                                <div className='section-title'>Istruzione del Padre</div>
                                <Istruzione
                                    submissions={submissions}
                                    question='question11'
                                    checkpoints={beginningCheckpoints}
                                />
                            </div>
                            <div>
                                <div className='section-title'>Istruzione della Madre</div>
                                <Istruzione
                                    submissions={submissions}
                                    question='question16'
                                    checkpoints={beginningCheckpoints}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            {loading && (
                <div className='loader-container'>
                    <Loader size={60}/>
                </div>
            )}
        </div>
    );
};

export default PavesiHomepage;
